<template>
  <div class="faq-page">
    <div class="hero-section">
      <div class="container">
        <h1>Veelgestelde Vragen | Tandarts Sint-Michielsgestel</h1>
        <p class="lead">Antwoorden op de meest gestelde vragen over tandheelkundige zorg bij Tandzorg Gestel in Sint-Michielsgestel en omgeving.</p>
      </div>
    </div>

    <div class="content-section">
      <div class="container">
        <div class="faq-categories">
          
          <!-- Locatie en Bereikbaarheid -->
          <div class="faq-category">
            <h2>📍 Locatie en Bereikbaarheid</h2>
            
            <div class="faq-item">
              <h3>Waar is Tandzorg Gestel gevestigd?</h3>
              <p>Tandzorg Gestel is gevestigd op <strong>Schijndelseweg 13A in Sint-Michielsgestel (5271 BK)</strong>. Wij zijn centraal gelegen in Gestel en goed bereikbaar vanuit Den Bosch, Vught, Boxtel en Schijndel.</p>
            </div>

            <div class="faq-item">
              <h3>Hoe ver is de tandarts vanaf Den Bosch centrum?</h3>
              <p>Vanuit het centrum van 's-Hertogenbosch bent u binnen <strong>10 minuten</strong> bij onze praktijk. Via de Vughterstraat en de A2 bent u er zo. Er is gratis parkeren mogelijk direct voor de deur.</p>
            </div>

            <div class="faq-item">
              <h3>Is er parkeergelegenheid bij de tandarts?</h3>
              <p>Ja, er zijn <strong>gratis parkeerplaatsen</strong> direct voor de praktijk beschikbaar. U hoeft zich geen zorgen te maken over parkeermunten of tijdslimieten. De praktijk is ook goed toegankelijk voor mindervaliden.</p>
            </div>

            <div class="faq-item">
              <h3>Welke buurten rondom Sint-Michielsgestel bedient u?</h3>
              <p>Naast Sint-Michielsgestel behandelen wij graag patiënten uit: <strong>Den Bosch</strong>, <strong>Vught</strong>, <strong>Boxtel</strong>, <strong>Schijndel</strong>, <strong>Rosmalen</strong>, <strong>Berlicum</strong> en <strong>Haaren</strong>. Alle omliggende plaatsen zijn binnen 15 minuten van onze praktijk.</p>
            </div>
          </div>

          <!-- Afspraken en Planning -->
          <div class="faq-category">
            <h2>📅 Afspraken en Planning</h2>

            <div class="faq-item">
              <h3>Hoe kan ik een afspraak maken bij tandarts Sint-Michielsgestel?</h3>
              <p>U kunt op verschillende manieren een afspraak maken bij Tandzorg Gestel:</p>
              <ul>
                <li><strong>Telefonisch:</strong> <a href="tel:+31735515644" class="contact-link">073-5515644</a></li>
                <li><strong>Online:</strong> Via onze website <router-link to="/afspraak-maken">afspraak maken</router-link></li>
                <li><strong>E-mail:</strong> <a href="mailto:info@tandzorggestel.nl" class="contact-link">info@tandzorggestel.nl</a></li>
                <li><strong>Inschrijven:</strong> Als nieuwe patiënt via <router-link to="/inschrijven">inschrijfformulier</router-link></li>
              </ul>
            </div>

            <div class="faq-item">
              <h3>Wat zijn de openingstijden van tandarts Gestel?</h3>
              <p>Tandzorg Gestel is geopend:</p>
              <ul>
                <li><strong>Maandag t/m donderdag:</strong> 08:15 - 17:00</li>
                <li><strong>Vrijdag:</strong> 08:00 - 12:30</li>
                <li><strong>Weekend:</strong> Gesloten (spoedhulp via 0900-1515)</li>
              </ul>
            </div>

            <div class="faq-item">
              <h3>Hoe snel kan ik terecht voor een afspraak?</h3>
              <p>Voor een <strong>reguliere controle</strong> kunt u meestal binnen 1-2 weken terecht. Voor <strong>spoedgevallen</strong> maken we altijd tijd vrij. <strong>Nieuwe patiënten</strong> zijn welkom en kunnen vaak binnen een week voor een kennismaking.</p>
            </div>

            <div class="faq-item">
              <h3>Accepteert u nieuwe patiënten in Sint-Michielsgestel?</h3>
              <p><strong>Ja, nieuwe patiënten zijn van harte welkom!</strong> Zowel uit Sint-Michielsgestel als uit de omliggende plaatsen zoals Den Bosch, Vught en Boxtel. U kunt zich eenvoudig online inschrijven of contact opnemen voor een kennismakingsafspraak.</p>
            </div>
          </div>

          <!-- Behandelingen en Diensten -->
          <div class="faq-category">
            <h2>🦷 Behandelingen en Diensten</h2>

            <div class="faq-item">
              <h3>Welke behandelingen biedt tandarts Sint-Michielsgestel?</h3>
              <p>Bij Tandzorg Gestel bieden wij een volledig scala aan tandheelkundige behandelingen:</p>
              <ul>
                <li><router-link to="/mondhygiene">Professionele mondhygiëne</router-link> en preventieve zorg</li>
                <li><router-link to="/cosmetische-tandheelkunde">Cosmetische tandheelkunde</router-link> (bleaching, veneers)</li>
                <li><router-link to="/wortelkanaalbehandeling">Wortelkanaalbehandelingen</router-link></li>
                <li>Vullingen en restauratieve tandheelkunde</li>
                <li>Kronen en bruggen</li>
                <li>Behandeling van angstpatiënten</li>
                <li>Aligner behandelingen (onzichtbare beugel)</li>
              </ul>
            </div>

            <div class="faq-item">
              <h3>Hebben jullie ervaring met angstpatiënten?</h3>
              <p><strong>Ja, zeker!</strong> Wij begrijpen dat niet iedereen graag naar de tandarts gaat. Ons team heeft veel ervaring met <strong>angstpatiënten</strong> en gebruikt speciale technieken om u op uw gemak te stellen. We nemen alle tijd en leggen alles rustig uit.</p>
            </div>

            <div class="faq-item">
              <h3>Doen jullie ook cosmetische behandelingen?</h3>
              <p>Ja, wij zijn gespecialiseerd in <router-link to="/cosmetische-tandheelkunde">cosmetische tandheelkunde</router-link>. Denk aan tandenbleken, veneers (facings), en andere behandelingen voor een <strong>stralende glimlach</strong>. We bieden eerst altijd een gratis consult aan.</p>
            </div>

            <div class="faq-item">
              <h3>Hoe pijnlijk is een wortelkanaalbehandeling?</h3>
              <p>Bij Tandzorg Gestel voeren we <router-link to="/wortelkanaalbehandeling">wortelkanaalbehandelingen</router-link> uit met moderne technieken en goede verdoving. <strong>De behandeling is pijnloos</strong> en we zorgen ervoor dat u zich comfortabel voelt tijdens de hele procedure.</p>
            </div>
          </div>

          <!-- Kosten en Verzekering -->
          <div class="faq-category">
            <h2>💶 Kosten en Verzekering</h2>

            <div class="faq-item">
              <h3>Wordt tandheelkundige zorg vergoed door mijn verzekering?</h3>
              <p>De meeste behandelingen vallen onder de <strong>basisverzekering</strong> of <strong>aanvullende tandartsverzekering</strong>. Wij werken samen met alle Nederlandse zorgverzekeraars en helpen graag bij het afhandelen van declaraties. Voor de exacte vergoeding kunt u contact opnemen met uw verzekeraar.</p>
            </div>

            <div class="faq-item">
              <h3>Kan ik in termijnen betalen?</h3>
              <p><strong>Ja, dat is mogelijk.</strong> Voor grotere behandelingen bieden wij verschillende betaalregelingen aan. Bespreek dit tijdens uw afspraak met ons team, dan zoeken we samen naar een geschikte oplossing.</p>
            </div>

            <div class="faq-item">
              <h3>Krijg ik vooraf een kostenopgave?</h3>
              <p><strong>Absoluut!</strong> Transparantie is belangrijk voor ons. Voor elke behandeling ontvangt u vooraf een duidelijke kostenopgave, inclusief informatie over wat er wel/niet vergoed wordt door uw verzekering.</p>
            </div>
          </div>

          <!-- Spoedhulp -->
          <div class="faq-category">
            <h2>🚨 Spoedhulp</h2>

            <div class="faq-item">
              <h3>Wat moet ik doen bij tandpijn buiten kantooruren?</h3>
              <p>Voor <strong>spoedgevallen buiten onze openingstijden</strong> kunt u terecht bij:</p>
              <ul>
                <li><strong>Mondzorg Poli 's-Hertogenbosch</strong></li>
                <li>📞 <a href="tel:0900-1515" class="contact-link">0900-1515</a> (24/7 bereikbaar)</li>
                <li>📍 Eekbrouwersweg 4, 's-Hertogenbosch</li>
              </ul>
              <p>Tijdens kantooruren belt u altijd eerst naar onze praktijk: <a href="tel:+31735515644" class="contact-link">073-5515644</a></p>
            </div>

            <div class="faq-item">
              <h3>Wat geldt als een spoedgeval bij de tandarts?</h3>
              <p>Spoedgevallen zijn situaties die <strong>niet kunnen wachten</strong> tot de volgende werkdag:</p>
              <ul>
                <li>Ernstige tandpijn die niet stopt</li>
                <li>Gebroken tand door ongeval</li>
                <li>Bloeding die niet stopt</li>
                <li>Gezwollen kaak of wang</li>
                <li>Uitgeslagen tand (bewaar in melk!)</li>
              </ul>
            </div>
          </div>

          <!-- Praktijk Informatie -->
          <div class="faq-category">
            <h2>🏥 Praktijk Informatie</h2>

            <div class="faq-item">
              <h3>Wat maakt Tandzorg Gestel bijzonder?</h3>
              <p>Tandzorg Gestel onderscheidt zich door:</p>
              <ul>
                <li><strong>Persoonlijke aandacht</strong> - u bent geen nummer</li>
                <li><strong>Moderne praktijk</strong> met nieuwste apparatuur</li>
                <li><strong>Ervaren team</strong> van tandartsen en mondhygiënisten</li>
                <li><strong>Lid van De Lieve Tandarts</strong> - extra kwaliteitswaarborg</li>
                <li><strong>Centrale ligging</strong> - makkelijk bereikbaar vanuit heel de regio</li>
                <li><strong>Gratis parkeren</strong> voor de deur</li>
              </ul>
            </div>

            <div class="faq-item">
              <h3>Zijn jullie aangesloten bij De Lieve Tandarts?</h3>
              <p><strong>Ja!</strong> Tandzorg Gestel is sinds kort <router-link to="/de-lieve-tandarts">lid geworden van De Lieve Tandarts</router-link>. Dit betekent dat wij voldoen aan de hoogste kwaliteitseisen op het gebied van behandeling, service en patiënttevredenheid.</p>
            </div>

            <div class="faq-item">
              <h3>Welke voorzorgsmaatregelen nemen jullie?</h3>
              <p>Hygiëne en veiligheid staan bij ons voorop. Wij hanteren strenge <strong>sterilisatie protocollen</strong>, gebruiken <strong>eenmalige materialen</strong> waar mogelijk, en houden ons aan alle richtlijnen van de RIVM en beroepsorganisaties.</p>
            </div>
          </div>

        </div>

        <!-- Contact CTA -->
        <div class="contact-cta">
          <h2>Heeft u nog andere vragen?</h2>
          <p>Stond uw vraag er niet bij? Neem gerust contact op met Tandzorg Gestel in Sint-Michielsgestel. Ons vriendelijke team helpt u graag verder!</p>
          <div class="cta-buttons">
            <router-link to="/contact" class="link-btn">Contact opnemen</router-link>
            <router-link to="/afspraak-maken" class="link-btn">Afspraak maken</router-link>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VeelgesteldeVragen',
  mounted() {
    document.title = "Veelgestelde Vragen | Tandarts Sint-Michielsgestel | Tandzorg Gestel";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Veelgestelde vragen over tandarts Sint-Michielsgestel. Tandzorg Gestel beantwoordt uw vragen over afspraken, behandelingen, kosten en bereikbaarheid vanuit Den Bosch.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'tandarts sint michielsgestel, tandarts gestel, veelgestelde vragen, FAQ tandarts, afspraak maken, nieuwe patiënten, Den Bosch tandarts');
    }

    // FAQ Schema markup
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Waar is Tandzorg Gestel gevestigd?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Tandzorg Gestel is gevestigd op Schijndelseweg 13A in Sint-Michielsgestel (5271 BK). Wij zijn centraal gelegen in Gestel en goed bereikbaar vanuit Den Bosch, Vught, Boxtel en Schijndel."
          }
        },
        {
          "@type": "Question", 
          "name": "Accepteert u nieuwe patiënten in Sint-Michielsgestel?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, nieuwe patiënten zijn van harte welkom! Zowel uit Sint-Michielsgestel als uit de omliggende plaatsen zoals Den Bosch, Vught en Boxtel."
          }
        },
        {
          "@type": "Question",
          "name": "Wat zijn de openingstijden van tandarts Gestel?",
          "acceptedAnswer": {
            "@type": "Answer", 
            "text": "Maandag t/m donderdag: 08:15 - 17:00, Vrijdag: 08:00 - 12:30. Weekend gesloten (spoedhulp via 0900-1515)."
          }
        }
      ]
    });
    document.head.appendChild(script);
  }
}
</script>

<style scoped>
.faq-page {
  padding-top: 88px;
}

.hero-section {
  background: linear-gradient(135deg, var(--blue), #0099a3);
  color: white;
  padding: 4rem 0;
  text-align: center;
}

.hero-section h1 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: bold;
}

.lead {
  font-size: 1.3rem;
  max-width: 600px;
  margin: 0 auto;
}

.content-section {
  padding: 4rem 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.faq-categories {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.faq-category {
  background: white;
  border-radius: 10px;
  padding: 3rem;
  box-shadow: var(--box-shadow);
}

.faq-category h2 {
  color: var(--blue);
  font-size: 2.4rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--light-bg);
}

.faq-item {
  margin-bottom: 2.5rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #eee;
}

.faq-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.faq-item h3 {
  color: var(--black);
  font-size: 1.8rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.faq-item p {
  color: var(--light-color);
  font-size: 1.6rem;
  line-height: 1.8;
  margin-bottom: 1rem;
}

.faq-item ul {
  margin: 1rem 0;
  padding-left: 2rem;
}

.faq-item li {
  color: var(--light-color);
  font-size: 1.6rem;
  line-height: 1.6;
  margin-bottom: 0.5rem;
}

.contact-cta {
  background: var(--light-bg);
  padding: 3rem;
  border-radius: 10px;
  text-align: center;
  margin-top: 4rem;
}

.contact-cta h2 {
  color: var(--blue);
  font-size: 2.4rem;
  margin-bottom: 1rem;
}

.contact-cta p {
  color: var(--light-color);
  font-size: 1.7rem;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-buttons {
  display: flex;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .hero-section h1 {
    font-size: 2.5rem;
  }
  
  .lead {
    font-size: 1.1rem;
  }
  
  .faq-category {
    padding: 2rem;
  }
  
  .faq-category h2 {
    font-size: 2rem;
  }
  
  .faq-item h3 {
    font-size: 1.6rem;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
}
</style>
